import {
	ApplicationRef,
	ComponentRef,
	EnvironmentInjector,
	Injectable,
	Type,
	createComponent,
} from '@angular/core';

/**
 * Motor de overlays próprio (sem Ionic). Renderiza modal/alert/loading/toast/popover
 * anexando um host no <body>, com backdrop + wrapper animados e uma pilha (getTop/dismiss
 * do topo). Substitui ModalController/AlertController/LoadingController/ToastController/
 * PopoverController do @ionic/angular mantendo a mesma API (create → present/dismiss/
 * onDidDismiss). Ver docs/desionizacao-ionic.md (Fase 1).
 */

export type OverlayRole = 'backdrop' | 'cancel' | 'gesture' | string;

export interface OverlayDismissEvent<T = any> {
	data?: T;
	role?: OverlayRole;
}

export interface OverlayHandle<T = any> {
	present(): Promise<void>;
	dismiss(data?: any, role?: OverlayRole): Promise<boolean>;
	onDidDismiss<D = T>(): Promise<OverlayDismissEvent<D>>;
	onWillDismiss<D = T>(): Promise<OverlayDismissEvent<D>>;
	readonly hostElement: HTMLElement;
	/** Compat: no Ionic o getTop() devolve o elemento do modal; delega ao host. */
	querySelector<E extends Element = Element>(selectors: string): E | null;
}

/** Os tipos de overlay. No Ionic cada um é um elemento próprio (`ion-modal`, `ion-loading`,
 *  …) com a sua própria pilha; aqui a pilha é uma só e o tipo é o que a separa. */
export type OverlayKind = 'modal' | 'alert' | 'loading' | 'toast' | 'popover';

export interface OverlayAttachConfig {
	kind: OverlayKind;
	cssClass?: string | string[];
	backdropDismiss?: boolean;
	showBackdrop?: boolean;
	animated?: boolean;
	duration?: number;
	position?: 'top' | 'bottom' | 'middle';
	/** Conteúdo por componente Angular (modal/popover). */
	component?: Type<any>;
	componentProps?: Record<string, any>;
	/** Conteúdo por DOM pré-montado (alert/loading/toast). */
	element?: HTMLElement;
	/** Âncora do popover (posição do clique). */
	positionTarget?: { x: number; y: number };
	/**
	 * Injector de ambiente de quem abriu o overlay (ver `MpModalController`). Sem ele o
	 * componente nasce no injector raiz e não enxerga os providers do módulo lazy da página.
	 */
	environmentInjector?: EnvironmentInjector;
}

const ENTER_MS = 280;
const LEAVE_MS = 200;

interface OverlayInstance {
	handle: OverlayHandle;
	kind: OverlayKind;
	hostEl: HTMLElement;
	componentRef?: ComponentRef<any>;
	previousActive?: HTMLElement | null;
	keyHandler?: (e: KeyboardEvent) => void;
	autoDismissTimer?: any;
}

@Injectable({ providedIn: 'root' })
export class OverlayService {
	private stack: OverlayInstance[] = [];

	constructor(
		private appRef: ApplicationRef,
		private envInjector: EnvironmentInjector,
	) {}

	/**
	 * Topo da pilha — do TIPO pedido, quando há um.
	 *
	 * O `kind` não é um detalhe: no Ionic cada controller tem a sua pilha, então
	 * `ModalController.getTop()` nunca devolve um `ion-loading`. Aqui a pilha é uma só, e sem
	 * o filtro um loading aberto por cima de um modal passava a ser "o modal do topo" — era o
	 * que fazia o `goDismiss()` do login fechar o loading, achar que já tinha fechado tudo e
	 * nunca navegar para o cardápio (QA OP-1075, C67). Ver overlay-pilha-por-tipo.spec.ts.
	 *
	 * Sem `kind` continua devolvendo o topo geral — é o que o guarda do Esc quer saber
	 * ("sou eu que estou na frente?"), e aí o tipo não importa.
	 */
	getTop(kind?: OverlayKind): OverlayHandle | undefined {
		const doTipo = kind ? this.stack.filter(i => i.kind === kind) : this.stack;
		return doTipo.length ? doTipo[doTipo.length - 1].handle : undefined;
	}

	/** Fecha o overlay do topo — do TIPO pedido, quando há um (ver getTop). */
	dismissTop(data?: any, role?: OverlayRole, kind?: OverlayKind): Promise<boolean> {
		const top = this.getTop(kind);
		if (!top) return Promise.resolve(false);
		return top.dismiss(data, role);
	}

	create(cfg: OverlayAttachConfig): OverlayHandle {
		const animated = cfg.animated !== false;
		const classes = Array.isArray(cfg.cssClass)
			? cfg.cssClass
			: cfg.cssClass
				? cfg.cssClass.split(/\s+/).filter(Boolean)
				: [];

		// --- host / backdrop / wrapper ---
		const hostEl = document.createElement('div');
		hostEl.className = ['mp-overlay', `mp-overlay--${cfg.kind}`, ...classes].join(' ');
		hostEl.setAttribute('role', 'dialog');
		hostEl.setAttribute('aria-modal', 'true');

		const backdropEl = document.createElement('div');
		backdropEl.className = 'mp-overlay-backdrop';
		if (cfg.showBackdrop === false) backdropEl.style.opacity = '0';

		const wrapperEl = document.createElement('div');
		wrapperEl.className = 'mp-overlay-wrapper';

		const contentEl = document.createElement('div');
		contentEl.className = 'mp-overlay-content';
		wrapperEl.appendChild(contentEl);

		hostEl.appendChild(backdropEl);
		hostEl.appendChild(wrapperEl);

		// --- conteúdo ---
		let componentRef: ComponentRef<any> | undefined;
		if (cfg.component) {
			componentRef = createComponent(cfg.component, { environmentInjector: cfg.environmentInjector ?? this.envInjector });
			if (cfg.componentProps) Object.assign(componentRef.instance, cfg.componentProps);
			this.appRef.attachView(componentRef.hostView);
			const el = componentRef.location.nativeElement as HTMLElement;
			el.classList.add('mp-overlay-page');
			contentEl.appendChild(el);
			componentRef.changeDetectorRef.detectChanges();
		} else if (cfg.element) {
			cfg.element.classList.add('mp-overlay-page');
			contentEl.appendChild(cfg.element);
		}

		// --- popover: posiciona junto ao alvo ---
		if (cfg.kind === 'popover' && cfg.positionTarget) {
			wrapperEl.style.position = 'fixed';
			wrapperEl.style.left = `${cfg.positionTarget.x}px`;
			wrapperEl.style.top = `${cfg.positionTarget.y}px`;
		}

		// --- promessas de dismiss ---
		let resolveDid: (v: OverlayDismissEvent) => void;
		let resolveWill: (v: OverlayDismissEvent) => void;
		const didDismiss = new Promise<OverlayDismissEvent>(r => (resolveDid = r));
		const willDismiss = new Promise<OverlayDismissEvent>(r => (resolveWill = r));

		let presented = false;
		let dismissed = false;

		const handle: OverlayHandle = {
			hostElement: hostEl,
			present: () => {
				if (presented) return Promise.resolve();
				presented = true;
				document.body.appendChild(hostEl);
				this.lockScroll();
				const inst = this.stack.find(i => i.hostEl === hostEl);
				if (inst) {
					inst.previousActive = document.activeElement as HTMLElement;
					// Toast não rouba foco (como no Ionic): tirar o foco de um campo fecharia o
					// teclado de quem está digitando quando o aviso aparece.
					if (cfg.kind !== 'toast') this.trapFocus(wrapperEl);
				}
				// força reflow antes de aplicar a classe de entrada (transição CSS)
				void hostEl.offsetWidth;
				hostEl.classList.add('mp-overlay--visible');
				if (cfg.duration && cfg.duration > 0 && inst) {
					inst.autoDismissTimer = setTimeout(() => handle.dismiss(undefined, 'timeout'), cfg.duration);
				}
				return this.wait(animated ? ENTER_MS : 0);
			},
			dismiss: (data?: any, role?: OverlayRole) => {
				if (dismissed) return Promise.resolve(false);
				dismissed = true;
				resolveWill({ data, role });
				const inst = this.stack.find(i => i.hostEl === hostEl);
				if (inst?.autoDismissTimer) clearTimeout(inst.autoDismissTimer);
				hostEl.classList.remove('mp-overlay--visible');
				hostEl.classList.add('mp-overlay--leaving');
				return this.wait(animated ? LEAVE_MS : 0).then(() => {
					this.teardown(hostEl, componentRef, inst);
					resolveDid({ data, role });
					return true;
				});
			},
			onDidDismiss: <D = any>() => didDismiss as Promise<OverlayDismissEvent<D>>,
			onWillDismiss: <D = any>() => willDismiss as Promise<OverlayDismissEvent<D>>,
			querySelector: <E extends Element = Element>(selectors: string) => hostEl.querySelector<E>(selectors),
		};

		const instance: OverlayInstance = { handle, kind: cfg.kind, hostEl, componentRef };
		this.stack.push(instance);

		// backdrop / esc
		if (cfg.backdropDismiss !== false) {
			backdropEl.addEventListener('click', () => handle.dismiss(undefined, 'backdrop'));
			instance.keyHandler = (e: KeyboardEvent) => {
				if (e.key === 'Escape' && this.getTop() === handle) handle.dismiss(undefined, 'backdrop');
			};
			document.addEventListener('keydown', instance.keyHandler);
		}

		return handle;
	}

	private teardown(hostEl: HTMLElement, componentRef?: ComponentRef<any>, inst?: OverlayInstance) {
		if (inst?.keyHandler) document.removeEventListener('keydown', inst.keyHandler);
		if (componentRef) {
			this.appRef.detachView(componentRef.hostView);
			componentRef.destroy();
		}
		hostEl.remove();
		this.stack = this.stack.filter(i => i.hostEl !== hostEl);
		if (inst?.previousActive && typeof inst.previousActive.focus === 'function') {
			inst.previousActive.focus();
		}
		if (!this.stack.length) this.unlockScroll();
	}

	/**
	 * Foco inicial do overlay, como o Ionic faz em `present()`: vai para o PRÓPRIO overlay
	 * (`tabindex="-1"`), não para o primeiro botão de dentro. Focar o primeiro botão desenhava
	 * o anel de foco em volta da seta de voltar de todo modal aberto (visível nos prints do QA
	 * OP-1075 do login e da tela de endereço); o Ionic só leva o foco para dentro quando a
	 * pessoa usa o Tab — e o Tab a partir do wrapper chega no primeiro focável pela ordem do DOM.
	 * `[autofocus]` explícito no conteúdo continua valendo.
	 */
	private trapFocus(wrapperEl: HTMLElement) {
		const explicito = wrapperEl.querySelector<HTMLElement>('[autofocus]');
		if (explicito) {
			explicito.focus?.();
			return;
		}
		if (!wrapperEl.hasAttribute('tabindex')) wrapperEl.setAttribute('tabindex', '-1');
		wrapperEl.focus?.({ preventScroll: true });
	}

	private lockScroll() {
		if (this.stack.length <= 1) document.body.classList.add('mp-overlay-open');
	}

	private unlockScroll() {
		document.body.classList.remove('mp-overlay-open');
	}

	private wait(ms: number): Promise<void> {
		return new Promise(resolve => setTimeout(resolve, ms));
	}
}
